// PO email template placeholder substitution.
//
// Templates contain `{{key}}` placeholders. Known placeholders ("auto-fill")
// resolve from SubstitutionContext; anything else is treated as a user variable
// that the Send PO dialog prompts for.

export interface POEmailTemplate {
  subject: string;
  body: string;
}

export interface SubstitutionContext {
  po: {
    number: string;
    order_date: string;
    expected_delivery_date: string;
    total_amount: string;
    total_units: number | string;
    items_count: number | string;
  };
  supplier: {
    name: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
  };
  merchant: {
    name: string;
  };
  today: string;
  userVariables: Record<string, string>;
}

// Matches {{ key }} where key is letters, digits, dots, underscores.
const PLACEHOLDER_RE = /\{\{\s*([a-z0-9_.]+)\s*\}\}/gi;

// Known auto-fill keys in dot-notation.
const AUTO_KEYS: readonly string[] = [
  'po.number',
  'po.order_date',
  'po.expected_delivery_date',
  'po.total_amount',
  'po.total_units',
  'po.items_count',
  'supplier.name',
  'supplier.contact_name',
  'supplier.contact_email',
  'supplier.contact_phone',
  'merchant.name',
  'today',
];

/**
 * Return all unique placeholder keys referenced in `text`, in order of first appearance.
 * Whitespace inside `{{ }}` is tolerated.
 */
export function extractPlaceholders(text: string): string[] {
  if (!text) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  let match: RegExpExecArray | null;
  const re = new RegExp(PLACEHOLDER_RE);
  while ((match = re.exec(text)) !== null) {
    const key = match[1].toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(key);
    }
  }
  return result;
}

export function listAutoKeys(): string[] {
  return [...AUTO_KEYS];
}

/**
 * Keys referenced in `template.subject` or `template.body` that are NOT
 * auto-fill keys. These become user-variable inputs in the Send PO dialog.
 */
export function listUserVariables(template: POEmailTemplate): string[] {
  const auto = new Set(AUTO_KEYS);
  const all = [
    ...extractPlaceholders(template.subject || ''),
    ...extractPlaceholders(template.body || ''),
  ];
  const user = all.filter((k) => !auto.has(k));
  return [...new Set(user)].sort();
}

type NamespacedRoot = 'po' | 'supplier' | 'merchant';
const NAMESPACED_ROOTS: readonly NamespacedRoot[] = ['po', 'supplier', 'merchant'];

function resolveKey(key: string, ctx: SubstitutionContext): string {
  if (key === 'today') return ctx.today || '';
  // User-variable lookup uses the full key, so dotted names like `ship.date` resolve.
  const user = ctx.userVariables ? ctx.userVariables[key] : undefined;
  if (user != null) return user;
  const parts = key.split('.');
  // Only exact 2-segment keys are structural lookups; anything else stays a placeholder
  // so malformed keys like `po.number.extra` don't silently resolve to `po.number`.
  if (parts.length !== 2) return `{{${key}}}`;
  const [root, leaf] = parts;
  if (!(NAMESPACED_ROOTS as readonly string[]).includes(root)) return `{{${key}}}`;
  const obj = ctx[root as NamespacedRoot] as Record<string, unknown>;
  if (obj == null) return `{{${key}}}`;
  const value = obj[leaf];
  return value != null ? String(value) : '';
}

/**
 * Render the template by substituting placeholders with values from `ctx`.
 * Unknown placeholders are preserved as `{{key}}` so the user can see what's missing.
 */
export function render(
  template: POEmailTemplate,
  ctx: SubstitutionContext,
): { subject: string; body: string } {
  const substitute = (text: string): string => {
    if (!text) return '';
    return text.replace(PLACEHOLDER_RE, (_m, rawKey: string) => resolveKey(rawKey.trim().toLowerCase(), ctx));
  };
  return {
    subject: substitute(template.subject || ''),
    body: substitute(template.body || ''),
  };
}

/**
 * Turn `ship_date` into "Ship Date" for a human-readable input label.
 */
export function humanizeVariableLabel(key: string): string {
  const last = key.split('.').pop() || key;
  return last
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
